import React from "react";
import { styled, useTheme } from '@mui/material/styles';
import { List, Typography, Divider, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse, Tooltip, Badge} from '@mui/material'
import MuiDrawer from '@mui/material/Drawer';
import MenuIconHandler from "./MenuIconHandler";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import TranslationContent from "../../Translations/TranslationContent";
import CompanyLogo from "../Utils/GlobalComponents/CompanyLogo";
import { useLocation } from "react-router-dom";
import { SessionContext } from "../../Context/SessionProvider";
import { DrawerHeader } from "./MainHeader";


const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  //overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerDesktop = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open, mobileOpen }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...((open || mobileOpen) && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const DesktopDrawer = ({ open, navigationList, handleLogout, handleNavigation, handleDrawerClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const session = React.useContext(SessionContext);

  return(
    <DrawerDesktop variant="permanent" open={open}>
      <DrawerHeader>
        <CompanyLogo width={150} showAppLogo={false}/>
        <IconButton onClick={handleDrawerClose} color="primary">
          {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List>
        {navigationList.basic.map((navigationItem, index) => (
          <ListItem key={navigationItem.section + index} disablePadding sx={{ display: 'block', }} color="primary">
            <Tooltip title={!open ? <TranslationContent contentID={navigationItem.section} /> : ""} arrow placement="right">
              <ListItemButton
                onClick={() => handleNavigation(navigationItem.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  color="primary"
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <MenuIconHandler navigationItem={navigationItem} /> 
                </ListItemIcon>      
                <ListItemText primary={<TranslationContent contentID={navigationItem.section} />} sx={{ opacity: open ? 1 : 0, color: location.pathname === navigationItem.path ? window.location.hostname.includes("ruukki") ? "#fc2516" : "#009fe3" : ""}} />
              </ListItemButton>
            </Tooltip>
          </ListItem> 
        ))}
      </List>    
      {navigationList.inspections.some(entry => entry !== null) && 
        <>
          <Divider />
          <Collapse in={open}>
            <Typography variant="caption" sx={{padding: "5px 0 0 20px"}}><b><TranslationContent contentID="inspections" /></b></Typography>
          </Collapse>
          <List>
            {navigationList.inspections.map((navigationItem, index) => ( navigationItem !== null &&
              <ListItem key={navigationItem + index} disablePadding sx={{ display: 'block', }} color="primary">
                <Tooltip title={!open ? <TranslationContent contentID={navigationItem.section} /> : ""} arrow placement="right">
                  <ListItemButton
                    onClick={() => handleNavigation(navigationItem.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      color="primary"
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      <MenuIconHandler navigationItem={navigationItem} />
                      {navigationItem.section === "newInspection" && !open && 
                        <Badge badgeContent={session.newInspections} variant="standard" color="primary" />
                      }
                      {navigationItem.section === "continueInspection" && !open && 
                        <Badge badgeContent={session.unfinishedInspections} variant="standard" color="primary" />
                      }
                    </ListItemIcon>
                    {navigationItem.section === "newInspection" ? 
                      <ListItemText 
                        primary={
                          <>
                            <TranslationContent contentID={navigationItem.section} />
                            <Badge badgeContent={session.newInspections} variant="standard" color="primary" sx={{position: "relative", top: -2, right: -15}} />
                          </>
                        } 
                        sx={{ opacity: open ? 1 : 0, color: location.pathname === navigationItem.path || 
                          (location.pathname.includes("/inspect/new/create") && navigationItem.path.includes("inspect/new")) || 
                          (location.pathname.includes("/inspect/new/select") && navigationItem.path.includes("inspect/new")) ? window.location.hostname.includes("ruukki") ? "#fc2516" : "#009fe3" : ""}} />
                    :
                      navigationItem.section === "continueInspection" ? 
                        <ListItemText primary={<><TranslationContent contentID={navigationItem.section} /><Badge badgeContent={session.unfinishedInspections} variant="standard" color="primary" sx={{position: "relative", top: -2, right: -15}} /></>} sx={{ opacity: open ? 1 : 0, color: location.pathname === navigationItem.path || (location.pathname.includes("inspect/active") && navigationItem.path.includes("inspect/active")) ? window.location.hostname.includes("ruukki") ? "#fc2516" : "#009fe3" : ""}} />
                      :
                        <ListItemText primary={<TranslationContent contentID={navigationItem.section} />} sx={{ opacity: open ? 1 : 0, color: location.pathname === navigationItem.path || (navigationItem.path.includes("active") && location.pathname.includes("active")) || (navigationItem.path.includes("/inspect/new") && location.pathname.includes("/inspect/new")) ? window.location.hostname.includes("ruukki") ? "#fc2516" : "#009fe3" : ""}} />
                    }
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </>
      }
      {navigationList.management.some(entry => entry !== null) && 
        <>
          <Divider />
          <Collapse in={open}>
            <Typography variant="caption" sx={{padding: "5px 0 0 20px"}}><b><TranslationContent contentID="management" /></b></Typography>
          </Collapse>
          <List>
            {navigationList.management.map((navigationItem, index) => ( navigationItem !== null &&
              <ListItem key={navigationItem + index} disablePadding sx={{ display: 'block', }} color="primary">
                <Tooltip title={!open ? <TranslationContent contentID={navigationItem.section} /> : ""} arrow placement="right">
                  <ListItemButton
                    onClick={() => handleNavigation(navigationItem.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      color="primary"
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      <MenuIconHandler navigationItem={navigationItem} />
                      {navigationItem.section === "userManagement" && !open && 
                        <Badge badgeContent={session.applications} variant="standard" color="primary" />
                      }
                    </ListItemIcon>
                    {navigationItem.section === "userManagement" ? 
                      <ListItemText primary={<><TranslationContent contentID={navigationItem.section} /><Badge badgeContent={session.applications} variant="standard" color="primary" sx={{position: "relative", top: -2, right: -15}} /></>} sx={{ opacity: open ? 1 : 0, color: location.pathname === navigationItem.path || (location.pathname.includes("users") && navigationItem.path.includes("users")) ? window.location.hostname.includes("ruukki") ? "#fc2516" : "#009fe3" : ""}} />
                    :
                      <ListItemText 
                        primary={
                          <TranslationContent contentID={navigationItem.section} />
                        } 
                        sx={{ 
                          opacity: open ? 1 : 0, 
                          color: location.pathname === navigationItem.path || 
                          (location.pathname.includes("users") && navigationItem.path.includes("users")) || 
                          (location.pathname.includes("form") && navigationItem.path.includes("form")) ? window.location.hostname.includes("ruukki") ? "#fc2516" : "#009fe3" : ""}} />
                    }
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </>
      }
      <Divider />
      <List>
        <ListItem key="logout-button-desktop" disablePadding sx={{ display: 'block', }}>
          <Tooltip title={!open ? <TranslationContent contentID="logout" /> : ""} arrow placement="right">
            <ListItemButton
              color="primary"
              onClick={handleLogout}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                color="primary"
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <LogoutIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary={<TranslationContent contentID="logout" />} sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </DrawerDesktop>
  )
}

export default DesktopDrawer;